import { type FunctionComponent, type PropsWithChildren, useContext, useEffect, useRef } from 'react';
import { MediaGalleryContext } from '../../../contexts/pdp/media-gallery.context';
import { doIfDefined } from '../../../helpers/general-helper/general-helper';
import { type Media } from '../../../helpers/images/images.helper';
import { handleKeys } from '../../../helpers/keyboard/keyboard.helper';
import { DEFAULT_CARD_WIDTH, useUpdateMediaProduct } from '../../../hooks/pdp/media-gallery/media-gallery.hooks';
import { CarouselButton } from '../../carousel/carousel.component';
import { cardSlider } from './thumbnail-carousel.css';
import { ThumbnailCard } from './thumbnail-components/thumbnail-components.component';

export type ThumbnailCarouselProps = {
	items: Media[];
	cardWrapper?: FunctionComponent<PropsWithChildren<{ index: number }>>;
	onSelectedImageChange?: (item: Media) => void;
	widthOfContentCardPixels?: number;
	slidesPerView: number;
};

export const ThumbnailCarousel: FunctionComponent<ThumbnailCarouselProps> = ({
	items,
	cardWrapper: CardWrapper,
	onSelectedImageChange,
	widthOfContentCardPixels = DEFAULT_CARD_WIDTH,
	slidesPerView
}) => {
	const { activeIndex, translateThumbnailPercentage, thumbnailTransitionSpeed, dispatch } = useContext(MediaGalleryContext);

	const { updateCarouselSlides } = useUpdateMediaProduct(items);
	const thumbnailCarouselRef = useRef<HTMLDivElement>(null);

	// Safari returns different values of scrollWidth, which causes weird behavior when swiping using both touch and arrow buttons
	// that's why it is needed to be set once
	const minOffsetXRef = useRef<number | null>(null);

	// Mobile touch events refs
	const touchStartXRef = useRef<number>(0);
	const touchMoveXRef = useRef<number>(0);
	const touchEndXRef = useRef<number>(0);

	const paddingOffset = 8;
	const borderOffset = 2;
	const lengthOfItems = items.length * widthOfContentCardPixels - paddingOffset + borderOffset;

	const scrollable = items.length > slidesPerView;

	const handleNavigateForward = () => {
		updateCarouselSlides(activeIndex + 1, widthOfContentCardPixels, slidesPerView);
		onSelectedImageChange?.(items[activeIndex + 1]);
	};

	const handleNavigateBack = () => {
		updateCarouselSlides(activeIndex - 1, widthOfContentCardPixels, slidesPerView);
		onSelectedImageChange?.(items[activeIndex - 1]);
	};

	const handleThumbnailClick = (selectedIndex: number) => {
		updateCarouselSlides(selectedIndex, widthOfContentCardPixels, slidesPerView);
		onSelectedImageChange?.(items[selectedIndex]);
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		event.preventDefault();
		if ((event.key === 'ArrowLeft' && activeIndex - 1 < 0) || (event.key === 'ArrowRight' && activeIndex + 1 > items.length - 1)) {
			event.stopPropagation();
		} else {
			if (event.key === 'ArrowLeft') {
				handleNavigateBack();
			} else if (event.key === 'ArrowRight') {
				handleNavigateForward();
			}
		}
	};

	const verifyOffsetValue = (offset: number, shouldUpdateTouchEndRef: boolean) => {
		doIfDefined(thumbnailCarouselRef.current, (carouselContainer) => {
			// set the value once
			let minOffsetX = minOffsetXRef.current;
			if (!minOffsetX) {
				minOffsetXRef.current = carouselContainer.offsetWidth - lengthOfItems;
				minOffsetX = minOffsetXRef.current;
			}
			if (minOffsetX) {
				let verifiedOffset = offset;
				const maxOffsetX = 0;
				// locks carousel at the beginning and end, if the offset is bigger than the max, then stop at the max and vice versa for the min
				// this may look reversed, but it's intentional because of the touch swipe gesture
				if (offset > maxOffsetX) {
					verifiedOffset = maxOffsetX;
				}
				if (offset < minOffsetX) {
					verifiedOffset = minOffsetX;
				}
				dispatch({ type: 'SET_TRANSLATE_THUMBNAIL_PERCENTAGE', payload: verifiedOffset });
				if (shouldUpdateTouchEndRef) {
					touchEndXRef.current = verifiedOffset;
				}
			}
		});
	};

	const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
		// touch transition needs to be faster with linear to keep up with the speed of the finger
		dispatch({ type: 'SET_THUMBNAIL_TRANSITION_SPEED', payload: 0.03 });
		touchStartXRef.current = e.targetTouches[0].clientX;
		touchMoveXRef.current = e.targetTouches[0].clientX;
	};

	const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
		touchMoveXRef.current = e.targetTouches[0].clientX;
		const translateDist = touchEndXRef.current + (touchMoveXRef.current - touchStartXRef.current);
		verifyOffsetValue(translateDist, false);
	};

	const handleTouchEnd = () => {
		// saves last position of the touch swipe for the next time
		touchEndXRef.current = translateThumbnailPercentage;
	};

	useEffect(() => {
		const carousel = thumbnailCarouselRef.current;
		if (carousel && carousel.offsetWidth > 0) {
			// resize of width required to update slidesPerView when changing browser size
			const resizeObserver = new ResizeObserver((entries) => {
				const entry = entries[0];
				dispatch({
					type: 'SET_THUMBNAIL_CAROUSEL_WIDTHS',
					payload: {
						thumbnailCarouselWidth: entry.contentRect.width,
						thumbnailMaxScrollWidth: entry.contentRect.width - lengthOfItems
					}
				});
			});

			resizeObserver.observe(carousel);
			return () => resizeObserver.unobserve(carousel);
		}
		return () => {};
	}, [items, widthOfContentCardPixels, lengthOfItems, dispatch]);

	const activeClasses = 'ba br2 b--theme-primary bw1';

	return (
		<div className={`flex items-center ${items.length === 5 ? 'justify-center' : ''}`}>
			{scrollable && (
				<div className="pr2">
					<CarouselButton arrowDirection="left" onClick={handleNavigateBack} shouldLock={activeIndex === 0} isMediaGallery />
				</div>
			)}

			<div
				ref={thumbnailCarouselRef}
				className="flex items-center overflow-hidden w-100"
				// only enable touch scrolling when there are more items than slidesPerView
				{...(scrollable && {
					onTouchStart: handleTouchStart,
					onTouchMove: handleTouchMove,
					onTouchEnd: handleTouchEnd
				})}>
				<div
					className={`flex h-100 gc2 ${cardSlider}`}
					style={{
						transform: `translate3d(${translateThumbnailPercentage}px, 0px, 0px)`,
						transition: `all ${thumbnailTransitionSpeed}s`
					}}
					data-testid="thumbnail-carousel">
					{items.map((item, index) => (
						<div
							className="flex items-center justify-center"
							style={{ flexShrink: 0, outline: 'none' }}
							key={`${item.id}-${index}`}
							onClick={() => handleThumbnailClick(index)}
							tabIndex={0}
							role="button"
							onKeyDown={handleKeys(['ArrowLeft', 'ArrowRight'], handleKeyDown)}
							data-testid={`thumbnail-carousel-item-${index}`}>
							{CardWrapper ? (
								<CardWrapper index={index}>
									<ThumbnailCard
										item={item}
										ariaLabel={`thumb slide ${index}`}
										className={`pa2 ${activeIndex === index && scrollable ? activeClasses : 'b--transparent'}`}
									/>
								</CardWrapper>
							) : (
								<ThumbnailCard
									item={item}
									ariaLabel={`thumb slide ${index}`}
									className={`pa2 ${activeIndex === index ? activeClasses : 'b--transparent'}`}
								/>
							)}
						</div>
					))}
				</div>
			</div>
			{scrollable && (
				<div className="pl2">
					<CarouselButton
						arrowDirection="right"
						onClick={handleNavigateForward}
						shouldLock={activeIndex === (items.length && items.length - 1)}
						isMediaGallery
					/>
				</div>
			)}
		</div>
	);
};
